import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, MapPin, Star, Users, CheckCircle, Sparkles, Crown } from "lucide-react";
import Footer from "@/components/Footer";

const Landing = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden noise-bg">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-charcoal/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="DuoLyst" className="h-12 w-auto object-contain" />
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-charcoal hover:bg-charcoal/5" data-testid="nav-login-btn">
                  Log in
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 btn-press" data-testid="nav-register-btn">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center text-center px-4 pt-20 pb-16 relative">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1708597786511-6d65ed5ffc91?q=80&w=2000&auto=format&fit=crop" 
            alt="Couple looking at mountain view"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto animate-fade-in">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/30 text-charcoal text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4 text-primary" />
            Turn dreams into shared memories
          </span>
          
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.1] text-charcoal mb-6">
            Your Shared Adventure
            <br />
            <span className="text-primary">Starts Here</span>
          </h1>
          
          <p className="text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-10">
            Create a bucket list together with your partner or friends. Dream big, track progress, 
            and celebrate every milestone on your journey together.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-10 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all btn-press"
                data-testid="hero-cta-btn"
              >
                Start Your List
              </Button>
            </Link>
            <Link to="/login">
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-charcoal text-charcoal hover:bg-charcoal hover:text-white rounded-full px-10 py-6 text-lg font-medium bg-transparent transition-all btn-press"
                data-testid="hero-login-btn"
              >
                I Have an Account
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-charcoal/30 flex items-start justify-center p-2">
            <div className="w-1.5 h-2.5 rounded-full bg-charcoal/30" />
          </div>
        </div>
      </section>

      {/* Founding 200 Banner - Top Position */}
      <section className="py-8 px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <Link to="/founding-explorer" className="block">
            <div className="bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border border-amber-400/30 rounded-2xl p-6 hover:shadow-lg hover:shadow-amber-500/10 transition-all group">
              <div className="flex items-center justify-center gap-3 mb-2">
                <Crown className="w-6 h-6 text-amber-500" />
                <span className="font-semibold text-amber-700 dark:text-amber-400 text-lg">
                  Join the Founding 200
                </span>
              </div>
              <p className="text-muted-foreground text-sm text-center">
                Limited spots for lifetime premium access. Free forever.
              </p>
              <span className="flex items-center justify-center gap-1 mt-3 text-primary text-sm font-medium group-hover:underline">
                Learn more →
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-charcoal mb-4">
              Everything You Need
            </h2>
            <p className="text-base leading-relaxed text-muted-foreground max-w-xl mx-auto">
              Simple yet powerful features to help you and your partner dream together
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {/* Feature 1 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Partner Linking
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Connect with your partner or friends using a simple invite code. Share your dreams instantly.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center mb-6">
                <MapPin className="w-6 h-6 text-secondary" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Organized Categories
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Travel, adventures, experiences, goals, romance - organize your list the way you want.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-accent/30 flex items-center justify-center mb-6">
                <Star className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Priority & Dates
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Set priorities and target dates to keep your goals on track and dreams in sight.
              </p>
            </div>

            {/* Feature 4 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-coral/10 flex items-center justify-center mb-6">
                <Heart className="w-6 h-6 text-coral" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Private & Shared
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Keep some items private or share everything. You control what your partner sees.
              </p>
            </div>

            {/* Feature 5 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-charcoal/10 flex items-center justify-center mb-6">
                <CheckCircle className="w-6 h-6 text-charcoal" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Track Progress
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                Celebrate together as you check off items. Watch your shared progress grow.
              </p>
            </div>

            {/* Feature 6 */}
            <div className="bg-card p-8 rounded-2xl border border-charcoal/5 shadow-card card-hover">
              <div className="w-12 h-12 rounded-xl bg-sage/10 flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-sage" />
              </div>
              <h3 className="font-serif text-xl font-medium text-charcoal mb-3">
                Beautiful Design
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                A warm, inviting space that makes planning feel special, not like a chore.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-card p-8 md:p-12 rounded-3xl border border-charcoal/5 shadow-float relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/5 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="font-serif text-3xl md:text-4xl font-medium tracking-tight text-charcoal mb-4">
                Ready to Start Your Journey?
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground max-w-xl mx-auto mb-8">
                Join couples and friends who are turning their shared dreams into reality.
              </p>
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-12 py-6 text-lg font-medium shadow-lg hover:shadow-xl transition-all btn-press"
                  data-testid="cta-register-btn"
                >
                  Create Your Free Account
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
